import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import {
  TIER_KINDS,
  TIER_KIND_LABELS,
  TIER_TARGET_MODE_LABELS,
  TIER_ACTION_LABELS,
  formatMinutes,
  type TierKind,
  type TierTargetMode,
  type TierAction,
  type OnChainExhausted,
  type EscalationPolicyWithTiers,
  type TierInput
} from "../lib/escalation.js";

/**
 * Tenant admin — Escalation Policy editor (ADR-0071 §4.2–§4.10).
 *
 * Route: `/app/admin/escalation-policies/:id`
 *
 *   - id = "new" → create mode: explicit "Create policy" button, redirects to list on success
 *   - else → edit mode: auto-saves on blur (text fields) or on change (selects/checkboxes/pickers)
 *
 * Two lifecycle tier cards: Acknowledgment and Resolution.
 */

type ExplicitAccount = { id: string; display_name: string };

type TierRow = {
  enabled: boolean;
  timerMinutesStr: string;
  targetMode: TierTargetMode;
  explicitAccounts: ExplicitAccount[];
  groupId: string;
  action: TierAction;
  onChainExhausted: OnChainExhausted;
  label: string;
};

const EMPTY_TIER_ROW: TierRow = {
  enabled: false,
  timerMinutesStr: "",
  targetMode: "chain_hop",
  explicitAccounts: [],
  groupId: "",
  action: "notify",
  onChainExhausted: "stop",
  label: ""
};

function emptyTierRows(): Record<TierKind, TierRow> {
  return {
    acknowledgment: { ...EMPTY_TIER_ROW },
    resolution: { ...EMPTY_TIER_ROW }
  };
}

const TIER_CARD_HELP: Record<TierKind, string> = {
  acknowledgment:
    "Escalate when nobody acknowledges the item. Active while the item is Open. The timer starts when the item is created and stops when the item is acknowledged or deferred.",
  resolution:
    "Escalate when work isn't getting done. Active while the item is Acknowledged or In Progress. The timer pauses while the item is Deferred and starts fresh when un-deferred."
};

const EXHAUSTION_LABEL: Record<TierTargetMode, string> = {
  chain_hop: "Keep notifying after chain exhausted",
  explicit: "Repeat notification",
  group: "Repeat notification"
};

const EXHAUSTION_HELP: Record<TierTargetMode, string> = {
  chain_hop:
    "When the escalation chain runs out of people, keep notifying the last person on every elapsed interval until the status changes. Off by default — the activity log captures the chain exhaustion.",
  explicit:
    "Keep notifying the selected users on every elapsed interval until the status changes. Off by default — fires once when the timer expires.",
  group:
    "Keep notifying the team on every elapsed interval until the status changes. Off by default — fires once when the timer expires."
};

type SaveStatus = "idle" | "saving" | "saved" | "error";

export function AdminEscalationPolicyEditor() {
  const params = useParams<{ id: string }>();
  const rawId = params.id ?? "new";
  const isNew = rawId === "new";
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [tiers, setTiers] = useState<Record<TierKind, TierRow>>(() => emptyTierRows());

  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [groupsLoadError, setGroupsLoadError] = useState(false);

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<TierKind | "name", string>>>({});
  const [busy, setBusy] = useState(false);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      type UsersRes = { users: Array<{ account_id: string; display_name: string | null; email: string; status: string }> };
      type GroupsRes = { groups: Array<{ id: string; name: string }> };
      type PolicyRes = { policy: EscalationPolicyWithTiers };

      const fetches: [Promise<UsersRes>, Promise<GroupsRes>, Promise<PolicyRes | null>] = [
        apiFetch<UsersRes>("/api/tenant/admin/users"),
        apiFetch<GroupsRes>("/api/tenant/admin/groups").catch(() => {
          setGroupsLoadError(true);
          return { groups: [] };
        }),
        isNew ? Promise.resolve(null) : apiFetch<PolicyRes>(`/api/tenant/admin/escalation-policies/${rawId}`)
      ];

      const [usersRes, groupsRes, policyRes] = await Promise.all(fetches);

      const memberCandidates: MemberCandidate[] = (usersRes.users ?? [])
        .filter((m) => m.status === "active")
        .map((m) => ({
          id: m.account_id,
          kind: "user" as const,
          display_name: m.display_name ?? m.email,
          email: m.email
        }));
      setCandidates(memberCandidates);

      const candidateById = new Map(memberCandidates.map((c) => [c.id, c]));

      setGroups((groupsRes.groups ?? []).map((g) => ({ id: g.id, name: g.name })));

      if (policyRes) {
        const p = policyRes.policy;
        setName(p.name);
        setDescription(p.description ?? "");
        setActive(p.active);
        const rows = emptyTierRows();
        for (const tier of p.tiers) {
          const kind = tier.kind as TierKind;
          if (kind !== "acknowledgment" && kind !== "resolution") continue;
          rows[kind] = {
            enabled: true,
            timerMinutesStr: String(tier.timer_minutes),
            targetMode: tier.target_mode as TierTargetMode,
            explicitAccounts: (tier.explicit_account_ids ?? []).map((id) => {
              const candidate = candidateById.get(id);
              return { id, display_name: candidate?.display_name ?? id };
            }),
            groupId: tier.group_id ?? "",
            action: tier.action as TierAction,
            onChainExhausted: tier.on_chain_exhausted as OnChainExhausted,
            label: tier.label ?? ""
          };
        }
        setTiers(rows);
      }
    } catch (e) {
      setLoadError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [rawId, isNew]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateTier(kind: TierKind, patch: Partial<TierRow>) {
    setTiers((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
    setFieldErrors((prev) => ({ ...prev, [kind]: undefined }));
  }

  // Build tiers payload from explicit values (not from state, which may be stale
  // when called synchronously inside an onChange handler).
  function buildTiersInputFrom(t: Record<TierKind, TierRow>): TierInput[] | null {
    const out: TierInput[] = [];
    const errs: Partial<Record<TierKind | "name", string>> = {};
    for (const kind of TIER_KINDS) {
      const row = t[kind];
      if (!row.enabled) continue;
      const mins = Number(row.timerMinutesStr);
      if (!Number.isInteger(mins) || mins <= 0) {
        errs[kind] = "Timer must be a positive whole number of minutes.";
        continue;
      }
      if (row.targetMode === "explicit" && row.explicitAccounts.length === 0) {
        errs[kind] = "At least one user is required.";
        continue;
      }
      if (row.targetMode === "group" && !row.groupId) {
        errs[kind] = "A team must be selected.";
        continue;
      }
      out.push({
        kind,
        timer_minutes: mins,
        target_mode: row.targetMode,
        explicit_account_ids: row.explicitAccounts.map((a) => a.id),
        group_id: row.targetMode === "group" ? row.groupId : null,
        action: row.action,
        on_chain_exhausted: row.onChainExhausted,
        label: row.label.trim() || null
      });
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...errs }));
      return null;
    }
    return out;
  }

  // Edit-mode save — accepts explicit values so onChange handlers can pass the
  // new value directly without waiting for React state to flush.
  async function performSave(opts: {
    name?: string;
    description?: string;
    active?: boolean;
    tiers?: Record<TierKind, TierRow>;
  } = {}) {
    if (isNew) return;
    const n = (opts.name ?? name).trim();
    const d = (opts.description ?? description).trim() || null;
    const a = opts.active ?? active;
    const t = opts.tiers ?? tiers;

    if (!n) {
      setFieldErrors((prev) => ({ ...prev, name: "Name is required." }));
      return;
    }
    const tiersInput = buildTiersInputFrom(t);
    if (tiersInput === null) return;

    setSaveStatus("saving");
    setSaveError(null);
    try {
      await apiFetch(`/api/tenant/admin/escalation-policies/${rawId}`, {
        method: "PATCH",
        body: { name: n, description: d, active: a }
      });
      await apiFetch(`/api/tenant/admin/escalation-policies/${rawId}/tiers`, {
        method: "PATCH",
        body: { tiers: tiersInput }
      });
      setSaveStatus("saved");
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      setSaveError(msg);
      setSaveStatus("error");
    }
  }

  async function create() {
    setFieldErrors({});
    const trimmed = name.trim();
    if (!trimmed) {
      setFieldErrors({ name: "Name is required." });
      return;
    }
    const tiersInput = buildTiersInputFrom(tiers);
    if (tiersInput === null) return;
    setBusy(true);
    try {
      await apiFetch<{ policy: EscalationPolicyWithTiers }>(
        "/api/tenant/admin/escalation-policies",
        {
          method: "POST",
          body: { name: trimmed, description: description.trim() || null, tiers: tiersInput }
        }
      );
      toast.push({ kind: "success", message: "Policy created." });
      navigate("/app/admin/escalation-policies");
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setBusy(false);
    }
  }

  const candidateById = useMemo(() => {
    const m = new Map<string, MemberCandidate>();
    for (const c of candidates) m.set(c.id, c);
    return m;
  }, [candidates]);

  const preview = useMemo(() => buildPreview(tiers, groups), [tiers, groups]);

  const breadcrumb = (
    <span>
      <Link to="/app/admin/escalation-policies" className="muted">
        Escalation Policies
      </Link>
      {" / "}
      {isNew ? "New policy" : name || "Edit policy"}
    </span>
  );

  const saveIndicator = !isNew && (
    <span className="muted" style={{ fontSize: 13 }}>
      {saveStatus === "saving" && "Saving…"}
      {saveStatus === "saved" && "Saved"}
      {saveStatus === "error" && (
        <span style={{ color: "var(--danger-text)" }}>Save failed — {saveError}</span>
      )}
    </span>
  );

  return (
    <PageLayout title={breadcrumb} actions={saveIndicator || undefined}>
      <div style={{ display: "grid", gap: 12 }}>
        {loadError && <ErrorState detail={loadError} />}
        {loading && !loadError && <LoadingState />}

        {!loading && !loadError && (
          <>
            {/* Policy metadata card */}
            <div className="card" style={{ display: "grid", gap: 12 }}>
              <div style={{ fontWeight: 700 }}>Policy</div>

              <label className="field">
                <span className="label">Name *</span>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  onBlur={() => void performSave({ name })}
                  placeholder="e.g. Critical SLA"
                />
                {fieldErrors.name && (
                  <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.name}</div>
                )}
              </label>

              <label className="field">
                <span className="label">Description</span>
                <textarea
                  className="input"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => void performSave({ description })}
                  placeholder="Optional — who this policy is for and why"
                />
              </label>

              {!isNew && (
                <label
                  className="field"
                  style={{ display: "flex", alignItems: "center", gap: 8, flexDirection: "row" }}
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setActive(next);
                      void performSave({ active: next });
                    }}
                  />
                  <span>Active</span>
                  <span className="muted" style={{ fontSize: 13 }}>
                    Inactive policies remain attached to existing action items but cannot be picked on new ones.
                  </span>
                </label>
              )}
            </div>

            {/* Tier cards */}
            {TIER_KINDS.map((kind) => {
              const row = tiers[kind];
              const mins = Number(row.timerMinutesStr);
              const minsValid = Number.isInteger(mins) && mins > 0;

              function updateAndSave(patch: Partial<TierRow>) {
                const nextTiers = {
                  ...tiers,
                  [kind]: { ...row, ...patch }
                };
                setTiers(nextTiers);
                setFieldErrors((prev) => ({ ...prev, [kind]: undefined }));
                void performSave({ tiers: nextTiers });
              }

              return (
                <div key={kind} className="card" style={{ display: "grid", gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{TIER_KIND_LABELS[kind]} escalation</div>
                    <div className="help" style={{ marginTop: 4 }}>{TIER_CARD_HELP[kind]}</div>
                  </div>

                  <label
                    className="field"
                    style={{ display: "flex", alignItems: "center", gap: 8, flexDirection: "row" }}
                  >
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(e) => updateAndSave({ enabled: e.target.checked })}
                    />
                    <span>Enable {TIER_KIND_LABELS[kind].toLowerCase()} escalation</span>
                  </label>

                  {row.enabled && (
                    <>
                      {/* 4-column grid: timer | target | action | label */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "120px 200px 180px 1fr",
                          gap: 10,
                          alignItems: "start"
                        }}
                      >
                        <div className="field">
                          <span className="label">Timer (min) *</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                              type="number"
                              className="input"
                              min={1}
                              value={row.timerMinutesStr}
                              onChange={(e) => {
                                updateTier(kind, { timerMinutesStr: e.target.value });
                                setFieldErrors((prev) => ({ ...prev, [kind]: undefined }));
                              }}
                              onBlur={() => {
                                const nextTiers = { ...tiers, [kind]: { ...row } };
                                void performSave({ tiers: nextTiers });
                              }}
                              placeholder="60"
                              style={{ minWidth: 0, flex: "1 1 0" }}
                            />
                            <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                              {minsValid ? `= ${formatMinutes(mins)}` : ""}
                            </span>
                          </div>
                        </div>

                        <div className="field">
                          <span className="label">Target</span>
                          <select
                            className="select"
                            value={row.targetMode}
                            onChange={(e) => updateAndSave({ targetMode: e.target.value as TierTargetMode })}
                          >
                            {(["chain_hop", "explicit", "group"] as TierTargetMode[]).map((m) => (
                              <option key={m} value={m}>{TIER_TARGET_MODE_LABELS[m]}</option>
                            ))}
                          </select>
                        </div>

                        <div className="field">
                          <span className="label">Action</span>
                          <select
                            className="select"
                            value={row.action}
                            onChange={(e) => updateAndSave({ action: e.target.value as TierAction })}
                          >
                            {(["notify", "reassign"] as TierAction[]).map((a) => (
                              <option key={a} value={a}>{TIER_ACTION_LABELS[a]}</option>
                            ))}
                          </select>
                        </div>

                        <div className="field">
                          <span className="label">Label</span>
                          <input
                            className="input"
                            value={row.label}
                            onChange={(e) => {
                              updateTier(kind, { label: e.target.value });
                            }}
                            onBlur={() => void performSave()}
                            placeholder="e.g. Manager alert"
                          />
                        </div>
                      </div>

                      {/* Target sub-fields */}
                      {row.targetMode === "explicit" && (
                        <div className="field">
                          <span className="label">Users *</span>
                          <MemberPicker
                            mode="multi"
                            allowedKinds={["user"]}
                            candidates={candidates}
                            selected={row.explicitAccounts.map((a) => a.id)}
                            onChange={(nextIds) => {
                              const nextAccounts: ExplicitAccount[] = nextIds.map((id) => {
                                const c = candidateById.get(id);
                                return { id, display_name: c?.display_name ?? id };
                              });
                              updateAndSave({ explicitAccounts: nextAccounts });
                            }}
                            placeholder="Search users…"
                          />
                        </div>
                      )}

                      {row.targetMode === "group" && (
                        <div className="field">
                          <span className="label">Team *</span>
                          {groupsLoadError ? (
                            <select className="select" disabled>
                              <option>Could not load teams.</option>
                            </select>
                          ) : (
                            <select
                              className="select"
                              value={row.groupId}
                              onChange={(e) => updateAndSave({ groupId: e.target.value })}
                            >
                              <option value="">Select a team…</option>
                              {groups.map((g) => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}

                      {/* Exhaustion toggle */}
                      <div>
                        <label
                          className="field"
                          style={{ display: "flex", alignItems: "center", gap: 8, flexDirection: "row" }}
                        >
                          <input
                            type="checkbox"
                            checked={row.onChainExhausted === "continue"}
                            onChange={(e) =>
                              updateAndSave({ onChainExhausted: e.target.checked ? "continue" : "stop" })
                            }
                          />
                          <span>{EXHAUSTION_LABEL[row.targetMode]}</span>
                        </label>
                        <div className="help">{EXHAUSTION_HELP[row.targetMode]}</div>
                      </div>

                      {/* Field error */}
                      {fieldErrors[kind] && (
                        <div className="help" style={{ color: "var(--danger-text)" }}>
                          {fieldErrors[kind]}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {/* Preview card */}
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Preview</div>
              {preview.length === 0 ? (
                <div className="muted">No escalation tiers enabled — this policy will not escalate.</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.map((line, i) => (
                    <li key={i} className="muted">{line}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Create mode actions only — edit mode has no buttons */}
            {isNew && (
              <div className="actions">
                <Link to="/app/admin/escalation-policies" className="btn">
                  Cancel
                </Link>
                <button
                  className="btn btnPrimary"
                  type="button"
                  onClick={() => void create()}
                  disabled={busy}
                >
                  {busy ? "Creating…" : "Create policy"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}

function buildPreview(
  tiers: Record<TierKind, TierRow>,
  groups: { id: string; name: string }[]
): string[] {
  const groupById = new Map(groups.map((g) => [g.id, g.name]));
  const out: string[] = [];
  for (const kind of TIER_KINDS) {
    const row = tiers[kind];
    if (!row.enabled) continue;
    const mins = Number(row.timerMinutesStr);
    if (!Number.isInteger(mins) || mins <= 0) continue;

    const timeStr = formatMinutes(mins);
    const isAck = kind === "acknowledgment";
    const tierLabel = TIER_KIND_LABELS[kind];
    const phaseVerb = isAck ? "unacknowledged" : "not resolved";
    const pauseSuffix = isAck ? "" : " Pauses while deferred.";

    let line = "";
    if (row.targetMode === "chain_hop") {
      const actionStr = row.action === "reassign" ? "reassign and notify" : "notify";
      const exhaust =
        row.onChainExhausted === "continue"
          ? `and after the chain is exhausted, keeps notifying the last person every ${timeStr} until ${isAck ? "acknowledged" : "resolved"}.`
          : "until the chain is exhausted, then stops.";
      line =
        `${tierLabel} (${timeStr}) — If ${phaseVerb} for ${timeStr}, ${actionStr} the next person up the escalation chain. ` +
        `Continues hop-by-hop ${exhaust} ` +
        `The original Accountable is also notified on each fire.${pauseSuffix}`;
    } else if (row.targetMode === "explicit") {
      const n = row.explicitAccounts.length;
      const names = row.explicitAccounts.map((a) => a.display_name).join(", ");
      const actionStr = row.action === "reassign" ? "reassign and notify" : "notify";
      const repeatStr =
        row.onChainExhausted === "continue"
          ? `Repeats every ${timeStr} until ${isAck ? "acknowledged" : "resolved"}.`
          : "Single fire.";
      line =
        `${tierLabel} (${timeStr}) — If ${phaseVerb} for ${timeStr}, ${actionStr} ${n} specific user${n === 1 ? "" : "s"}` +
        (names ? ` (${names})` : "") +
        `. ${repeatStr} The original Accountable is also notified.${pauseSuffix}`;
    } else {
      const groupName = groupById.get(row.groupId) ?? "selected team";
      const actionStr = row.action === "reassign" ? "reassign and notify" : "notify";
      const repeatStr =
        row.onChainExhausted === "continue"
          ? `Repeats every ${timeStr} until ${isAck ? "acknowledged" : "resolved"}.`
          : "Single fire.";
      line =
        `${tierLabel} (${timeStr}) — If ${phaseVerb} for ${timeStr}, ${actionStr} the ${groupName} team. ` +
        `${repeatStr} The original Accountable is also notified on each fire.${pauseSuffix}`;
    }
    out.push(line);
  }
  return out;
}
